import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger
} from '../components/ui/collapsible';
import { 
  Users, Save, Loader2, RefreshCw, ChevronDown, ChevronRight, Plus, Calendar,
  Baby, User, UserPlus, Briefcase, AlertTriangle, Eye, History, CheckCircle2,
  BarChart3, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, BarChart, Bar
} from 'recharts';

const CATEGORIA_ICONS = {
  'Infância': Baby,
  'Idosos': User,
  'Família': Users,
  'Deficiência': UserPlus,
  'Saúde Mental': Briefcase,
  'Dependências': Briefcase,
  'VIH/SIDA': Briefcase,
  'Violência Doméstica': Briefcase,
  'Reclusos': Briefcase,
  'Migrantes': Briefcase,
  'Saúde': Briefcase,
  'Educação': Briefcase,
  'Emprego': Briefcase,
  'Habitação': Briefcase,
  'Desporto': Briefcase,
  'Cultura': Briefcase,
  'Apoio Material': Briefcase,
  'Transporte': Briefcase,
  'Telecomunicações': Briefcase,
  'Outras': Briefcase
};

const CATEGORIA_NAMES = {
  'Infância': 'Infância e Juventude',
  'Idosos': 'População Idosa',
  'Família': 'Família e Comunidade',
  'Deficiência': 'Deficiência',
  'Saúde Mental': 'Saúde Mental',
  'Dependências': 'Dependências',
  'VIH/SIDA': 'VIH/SIDA',
  'Violência Doméstica': 'Violência Doméstica',
  'Reclusos': 'Reclusos e Ex-Reclusos',
  'Migrantes': 'Migrantes e Refugiados',
  'Saúde': 'Saúde',
  'Educação': 'Educação e Formação',
  'Emprego': 'Emprego',
  'Habitação': 'Habitação',
  'Desporto': 'Desporto e Lazer',
  'Cultura': 'Cultura',
  'Apoio Material': 'Apoio Material',
  'Transporte': 'Transporte',
  'Telecomunicações': 'Telecomunicações',
  'Outras': 'Outras Respostas'
};

export default function PortalValenciasPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [valencias, setValencias] = useState([]);
  const [porCategoria, setPorCategoria] = useState({});
  const [resumo, setResumo] = useState({});
  const [utentes, setUtentes] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValencias, setOriginalValencias] = useState([]);
  
  // Collapsible states - only inactive categories start collapsed
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Confirmation dialog
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  
  // History modal
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  
  // Modal estados
  const [utentesModalOpen, setUtentesModalOpen] = useState(false);
  const [utentesForm, setUtentesForm] = useState({
    periodo_referencia: new Date().toISOString().slice(0, 7),
    bebes_0_12m_masculino: 0,
    bebes_0_12m_feminino: 0,
    bebes_13_24m_masculino: 0,
    bebes_13_24m_feminino: 0,
    criancas_3_5a_masculino: 0,
    criancas_3_5a_feminino: 0,
    criancas_6_10a_masculino: 0,
    criancas_6_10a_feminino: 0,
    jovens_11_14a_masculino: 0,
    jovens_11_14a_feminino: 0,
    jovens_15_18a_masculino: 0,
    jovens_15_18a_feminino: 0,
    adultos_18plus_masculino: 0,
    adultos_18plus_feminino: 0,
    idosos_65plus_masculino: 0,
    idosos_65plus_feminino: 0
  });
  
  // States for demographic modal per valencia
  const [selectedValencia, setSelectedValencia] = useState(null);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({ masculino: 0, feminino: 0 });
  
  // States for dynamic faixas etárias
  const [valenciaFaixas, setValenciaFaixas] = useState([]);
  const [loadingFaixas, setLoadingFaixas] = useState(false);
  const [demoFaixasForm, setDemoFaixasForm] = useState({});

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadData();
  }, []);

  // Initialize expanded categories based on which have active valencias
  useEffect(() => {
    if (Object.keys(porCategoria).length > 0) {
      const expanded = {};
      Object.entries(porCategoria).forEach(([cat, items]) => {
        // Expand categories that have at least one active valencia
        const hasActive = items.some(v => {
          const found = valencias.find(val => val.valencia_id === v.valencia_id);
          return found?.ativa;
        });
        expanded[cat] = hasActive;
      });
      setExpandedCategories(expanded);
    }
  }, [porCategoria]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [valRes, utentesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/valencias`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/utentes`, { headers })
      ]);

      if (valRes.ok) {
        const data = await valRes.json();
        setValencias(data.valencias || []);
        setOriginalValencias(JSON.parse(JSON.stringify(data.valencias || [])));
        setPorCategoria(data.por_categoria || {});
        setResumo(data.resumo || {});
      } else if (valRes.status === 401) {
        navigate('/portal/login');
        return;
      }

      if (utentesRes.ok) {
        const utentesData = await utentesRes.json();
        setUtentes(utentesData || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoria) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoria]: !prev[categoria]
    }));
  };

  const toggleValencia = (valenciaId) => {
    setValencias(prev => prev.map(v => 
      v.valencia_id === valenciaId 
        ? { ...v, ativa: !v.ativa, numero_utentes: !v.ativa ? v.numero_utentes : 0 }
        : v
    ));
    setHasChanges(true);
  };

  const updateValenciaField = (valenciaId, field, value) => {
    setValencias(prev => prev.map(v =>
      v.valencia_id === valenciaId
        ? { ...v, [field]: Math.max(0, parseInt(value) || 0) }
        : v
    ));
    setHasChanges(true);
  };

  const saveValenciaDemographics = () => {
    if (!selectedValencia) return;
    
    // Se temos faixas dinâmicas, usar os totais calculados
    if (valenciaFaixas.length > 0) {
      const totais = calcularTotaisFaixas();
      
      setValencias(prev => prev.map(v =>
        v.valencia_id === selectedValencia.valencia_id
          ? { 
              ...v, 
              utentes_masculino: totais.masculino,
              utentes_feminino: totais.feminino,
              numero_utentes: totais.total > 0 ? totais.total : v.numero_utentes,
              ...demoFaixasForm // Include all faixa fields
            }
          : v
      ));
    } else {
      // Fallback para formulário simples
      const total = demoForm.masculino + demoForm.feminino;
      
      setValencias(prev => prev.map(v =>
        v.valencia_id === selectedValencia.valencia_id
          ? { 
              ...v, 
              utentes_masculino: demoForm.masculino,
              utentes_feminino: demoForm.feminino,
              numero_utentes: total > 0 ? total : v.numero_utentes
            }
          : v
      ));
    }
    
    setHasChanges(true);
    setDemoModalOpen(false);
    toast.success('Dados demográficos atualizados');
  };
  
  // Function to open demographic modal with dynamic faixas
  const openDemoModal = async (valencia) => {
    setSelectedValencia(valencia);
    setDemoForm({
      masculino: valencia.utentes_masculino || 0,
      feminino: valencia.utentes_feminino || 0
    });
    
    // Load faixas etárias específicas para esta resposta social
    setLoadingFaixas(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/demografia/valencias/${valencia.valencia_id}/faixas-etarias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const faixas = data.faixas_etarias || [];
        setValenciaFaixas(faixas);
        
        // Initialize form with existing data or zeros
        const formInit = {};
        faixas.forEach(f => {
          formInit[`${f.codigo}_masculino`] = valencia[`${f.codigo}_masculino`] || 0;
          formInit[`${f.codigo}_feminino`] = valencia[`${f.codigo}_feminino`] || 0;
        });
        setDemoFaixasForm(formInit);
      } else {
        // Fallback - usar todas as faixas
        setValenciaFaixas([]);
      }
    } catch (error) {
      console.error('Error loading faixas for valencia:', error);
      setValenciaFaixas([]);
    } finally {
      setLoadingFaixas(false);
    }
    
    setDemoModalOpen(true);
  };
  
  // Calculate totals from faixas form
  const calcularTotaisFaixas = () => {
    let masculino = 0;
    let feminino = 0;
    
    Object.entries(demoFaixasForm).forEach(([key, value]) => {
      if (key.endsWith('_masculino')) {
        masculino += value || 0;
      } else if (key.endsWith('_feminino')) {
        feminino += value || 0;
      }
    });
    
    return { masculino, feminino, total: masculino + feminino };
  };

  // Get changes summary for confirmation dialog
  const getChangesSummary = () => {
    const changes = [];
    valencias.forEach(v => {
      const original = originalValencias.find(ov => ov.valencia_id === v.valencia_id);
      if (original) {
        if (original.ativa !== v.ativa) {
          changes.push({
            valencia: v.nome,
            tipo: v.ativa ? 'Ativada' : 'Desativada',
            cor: v.ativa ? 'text-green-600' : 'text-red-600'
          });
        } else if (v.ativa && (original.numero_utentes !== v.numero_utentes || original.capacidade !== v.capacidade)) {
          changes.push({
            valencia: v.nome,
            tipo: `Alteração de dados (${original.numero_utentes}→${v.numero_utentes} utentes)`,
            cor: 'text-blue-600'
          });
        }
      }
    });
    return changes;
  };

  const handleSaveClick = () => {
    // Open confirmation dialog
    setConfirmSaveOpen(true);
  };

  const handleSaveValencias = async () => {
    setConfirmSaveOpen(false);
    setSaving(true);
    try {
      const token = localStorage.getItem('portal_token');
      
      // Validate
      for (const v of valencias.filter(v => v.ativa)) {
        if (v.numero_utentes > v.capacidade && v.capacidade > 0) {
          toast.error(`${v.nome}: Nº utentes não pode exceder capacidade`);
          setSaving(false);
          return;
        }
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/valencias`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(valencias.map(v => ({
          valencia_id: v.valencia_id,
          codigo: v.codigo,
          ativa: v.ativa,
          numero_utentes: v.numero_utentes,
          capacidade: v.capacidade,
          utentes_masculino: v.utentes_masculino,
          utentes_feminino: v.utentes_feminino
        })))
      });

      if (response.ok) {
        toast.success('Respostas sociais atualizadas com sucesso!');
        setHasChanges(false);
        setOriginalValencias(JSON.parse(JSON.stringify(valencias)));
        loadData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao guardar');
      }
    } catch (error) {
      toast.error('Erro ao guardar respostas sociais');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitUtentes = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('portal_token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/utentes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(utentesForm)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Dados submetidos! Total: ${data.total_utentes} utentes`);
        setUtentesModalOpen(false);
        loadData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao submeter');
      }
    } catch (error) {
      toast.error('Erro ao submeter dados');
    } finally {
      setSaving(false);
    }
  };

  const updateUtentesField = (field, value) => {
    setUtentesForm(prev => ({ ...prev, [field]: Math.max(0, parseInt(value) || 0) }));
  };

  const calcularTotais = () => {
    const totalMasc = 
      utentesForm.bebes_0_12m_masculino + utentesForm.bebes_13_24m_masculino +
      utentesForm.criancas_3_5a_masculino + utentesForm.criancas_6_10a_masculino +
      utentesForm.jovens_11_14a_masculino + utentesForm.jovens_15_18a_masculino +
      utentesForm.adultos_18plus_masculino + utentesForm.idosos_65plus_masculino;
    
    const totalFem = 
      utentesForm.bebes_0_12m_feminino + utentesForm.bebes_13_24m_feminino +
      utentesForm.criancas_3_5a_feminino + utentesForm.criancas_6_10a_feminino +
      utentesForm.jovens_11_14a_feminino + utentesForm.jovens_15_18a_feminino +
      utentesForm.adultos_18plus_feminino + utentesForm.idosos_65plus_feminino;
    
    return { masculino: totalMasc, feminino: totalFem, total: totalMasc + totalFem };
  };

  // Calculate current totals from valencias
  const calcularResumoAtual = () => {
    const ativas = valencias.filter(v => v.ativa);
    const totalUtentes = ativas.reduce((sum, v) => sum + (v.numero_utentes || 0), 0);
    const totalCapacidade = ativas.reduce((sum, v) => sum + (v.capacidade || 0), 0);
    const totalMasculino = ativas.reduce((sum, v) => sum + (v.utentes_masculino || 0), 0);
    const totalFeminino = ativas.reduce((sum, v) => sum + (v.utentes_feminino || 0), 0);
    
    // Check for valencias without demographic data
    const valenciaSemDados = ativas.filter(v => 
      v.numero_utentes > 0 && 
      ((v.utentes_masculino || 0) + (v.utentes_feminino || 0)) === 0
    );
    
    return {
      total_valencias: ativas.length,
      total_utentes: totalUtentes,
      total_capacidade: totalCapacidade,
      total_masculino: totalMasculino,
      total_feminino: totalFeminino,
      taxa_ocupacao: totalCapacidade > 0 ? Math.round((totalUtentes / totalCapacidade) * 100) : 0,
      valencias_sem_dados: valenciaSemDados
    };
  };

  // View historical item details
  const viewHistoryItem = (item) => {
    setSelectedHistoryItem(item);
    setHistoryModalOpen(true);
  };

  const resumoAtual = calcularResumoAtual();

  // Prepare chart data from utentes history
  const prepareChartData = () => {
    if (!utentes || utentes.length === 0) return [];
    
    return utentes
      .slice(0, 12) // Last 12 periods
      .reverse()
      .map(u => ({
        periodo: u.periodo_referencia,
        masculino: u.total_masculino || 0,
        feminino: u.total_feminino || 0,
        total: (u.total_masculino || 0) + (u.total_feminino || 0)
      }));
  };

  const chartData = prepareChartData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="valencias-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-valencias-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-[#0066CC]" />
            Utentes e Respostas Sociais
          </h1>
          <p className="text-slate-500">Gerir respostas sociais e dados demográficos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setUtentesModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Dados Demográficos
          </Button>
          {hasChanges && (
            <Button onClick={handleSaveClick} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="valencias" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="valencias" className="data-[state=active]:bg-white">
            <Users className="w-4 h-4 mr-2" />
            Gerir Respostas Sociais
          </TabsTrigger>
          <TabsTrigger value="evolucao" className="data-[state=active]:bg-white">
            <BarChart3 className="w-4 h-4 mr-2" />
            Evolução Demográfica
          </TabsTrigger>
        </TabsList>

        <TabsContent value="valencias" className="space-y-4">
          {/* Alert for valencias without demographic data */}
          {resumoAtual.valencias_sem_dados.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Respostas sociais sem dados demográficos</p>
                  <p className="text-sm text-amber-700 mt-1">
                    As seguintes respostas sociais ativas não têm dados demográficos preenchidos:
                  </p>
              <ul className="mt-2 space-y-1">
                {resumoAtual.valencias_sem_dados.map((v, i) => (
                  <li key={i} className="text-sm text-amber-700 flex items-center gap-2">
                    <span>•</span>
                    <span className="font-medium">{v.nome}</span>
                    <span className="text-amber-500">({v.numero_utentes} utentes)</span>
                    <Button 
                      size="sm" 
                      variant="link" 
                      className="h-auto p-0 text-amber-700 underline"
                      onClick={() => openDemoModal(v)}
                    >
                      Preencher
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3 space-y-4">
          {Object.entries(porCategoria).map(([categoria, items]) => {
            const Icon = CATEGORIA_ICONS[categoria] || Users;
            const categoriaNome = CATEGORIA_NAMES[categoria] || categoria;
            const ativas = items.filter(v => {
              const found = valencias.find(val => val.valencia_id === v.valencia_id);
              return found?.ativa;
            }).length;
            const isExpanded = expandedCategories[categoria] ?? false;
            
            return (
              <Collapsible 
                key={categoria} 
                open={isExpanded}
                onOpenChange={() => toggleCategory(categoria)}
                className="border rounded-lg bg-white"
              >
                <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0066CC]/10 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#0066CC]" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">{categoriaNome}</p>
                      <p className="text-sm text-slate-500">
                        {ativas > 0 ? (
                          <span className="text-green-600 font-medium">{ativas} ativas</span>
                        ) : (
                          <span className="text-slate-400">Nenhuma ativa</span>
                        )} de {items.length} disponíveis
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {ativas > 0 && (
                      <Badge className="bg-green-100 text-green-700">{ativas}</Badge>
                    )}
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent className="px-4 pb-4">
                  <div className="space-y-3 pt-2">
                    {/* Show active valencias first */}
                    {items
                      .map((valencia) => {
                        const v = valencias.find(val => val.valencia_id === valencia.valencia_id) || valencia;
                        return v;
                      })
                      .sort((a, b) => {
                        // Sort active first
                        if (a.ativa && !b.ativa) return -1;
                        if (!a.ativa && b.ativa) return 1;
                        return 0;
                      })
                      .map((v) => {
                        const totalDemo = (v.utentes_masculino || 0) + (v.utentes_feminino || 0);
                        const demoMismatch = v.ativa && v.numero_utentes > 0 && totalDemo > 0 && totalDemo !== v.numero_utentes;
                        const needsDemoData = v.ativa && v.numero_utentes > 0 && totalDemo === 0;
                        
                        return (
                          <div 
                            key={v.valencia_id}
                            className={`p-3 rounded-lg border ${
                              v.ativa 
                                ? needsDemoData 
                                  ? 'bg-amber-50 border-amber-200' 
                                  : 'bg-blue-50 border-blue-200' 
                                : 'bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <Switch
                                checked={v.ativa}
                                onCheckedChange={() => toggleValencia(v.valencia_id)}
                                data-testid={`valencia-toggle-${v.codigo}`}
                              />
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium ${!v.ativa && 'text-slate-400'}`}>
                                  {v.codigo} - {v.nome}
                                </p>
                                {v.ativa && (
                                  <div className="flex items-center gap-2 mt-1">
                                    {totalDemo > 0 ? (
                                      <span className="text-xs text-slate-500 flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                                        {v.utentes_masculino || 0} ♂ / {v.utentes_feminino || 0} ♀
                                      </span>
                                    ) : v.numero_utentes > 0 ? (
                                      <span className="text-xs text-amber-600 flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3" />
                                        Dados demográficos em falta
                                      </span>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                              {v.ativa && (
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-2">
                                    <Label className="text-xs text-slate-500 whitespace-nowrap">Utentes</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      className={`w-20 h-8 ${demoMismatch || needsDemoData ? 'border-amber-500' : ''}`}
                                      value={v.numero_utentes}
                                      onChange={(e) => updateValenciaField(v.valencia_id, 'numero_utentes', e.target.value)}
                                      data-testid={`valencia-utentes-${v.codigo}`}
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Label className="text-xs text-slate-500 whitespace-nowrap">Cap.</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      className="w-20 h-8"
                                      value={v.capacidade}
                                      onChange={(e) => updateValenciaField(v.valencia_id, 'capacidade', e.target.value)}
                                      data-testid={`valencia-capacidade-${v.codigo}`}
                                    />
                                  </div>
                                  <Button
                                    size="sm"
                                    variant={needsDemoData ? "default" : "outline"}
                                    className={`h-8 ${needsDemoData ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                                    onClick={() => openDemoModal(v)}
                                    data-testid={`valencia-demo-${v.codigo}`}
                                  >
                                    <span className="text-blue-500 mr-0.5">♂</span>
                                    <span className="text-pink-500">♀</span>
                                  </Button>
                                </div>
                              )}
                            </div>
                            {demoMismatch && (
                              <div className="mt-2 flex items-center gap-1 text-xs text-amber-600">
                                <AlertTriangle className="w-3 h-3" />
                                Total demográfico ({totalDemo}) difere do nº de utentes ({v.numero_utentes})
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>

        {/* Sidebar Resumo */}
        <div className="space-y-4">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle className="text-lg">Resumo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Respostas Sociais Ativas</span>
                  <span className="font-semibold text-green-600">{resumoAtual.total_valencias}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total Utentes</span>
                  <span className="font-semibold">{resumoAtual.total_utentes}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Capacidade Total</span>
                  <span className="font-semibold">{resumoAtual.total_capacidade}</span>
                </div>
                <div className="h-px bg-slate-200 my-2" />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Masculino</span>
                  <span className="font-semibold text-blue-600">{resumoAtual.total_masculino}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Feminino</span>
                  <span className="font-semibold text-pink-600">{resumoAtual.total_feminino}</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Taxa Ocupação</span>
                  <span className={`font-semibold ${
                    resumoAtual.taxa_ocupacao > 90 ? 'text-red-600' :
                    resumoAtual.taxa_ocupacao > 75 ? 'text-amber-600' : 'text-green-600'
                  }`}>
                    {resumoAtual.taxa_ocupacao}%
                  </span>
                </div>
                <Progress 
                  value={resumoAtual.taxa_ocupacao} 
                  className={`h-2 ${
                    resumoAtual.taxa_ocupacao > 90 ? '[&>div]:bg-red-500' :
                    resumoAtual.taxa_ocupacao > 75 ? '[&>div]:bg-amber-500' : '[&>div]:bg-green-500'
                  }`}
                />
              </div>

              {/* Warnings */}
              {resumoAtual.valencias_sem_dados.length > 0 && (
                <div className="bg-amber-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {resumoAtual.valencias_sem_dados.length} resposta(s) social(ais) sem dados demográficos
                    </span>
                  </div>
                </div>
              )}

              {hasChanges && (
                <Button 
                  onClick={handleSaveClick} 
                  disabled={saving} 
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="save-valencias-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Guardar Alterações
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Últimos Dados Demográficos */}
          {utentes.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Histórico Demográfico
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {utentes.slice(0, 5).map((u, i) => (
                    <button 
                      key={i} 
                      onClick={() => viewHistoryItem(u)}
                      className="w-full flex items-center justify-between text-sm p-2 bg-slate-50 rounded hover:bg-slate-100 transition-colors text-left"
                      data-testid={`history-item-${i}`}
                    >
                      <div>
                        <span className="font-medium">{u.periodo_referencia}</span>
                        <div className="text-xs text-slate-500">
                          {u.total_masculino || 0}♂ / {u.total_feminino || 0}♀
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{u.total_utentes} utentes</Badge>
                        <Eye className="w-4 h-4 text-slate-400" />
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      </TabsContent>

      {/* Tab: Evolução Demográfica */}
      <TabsContent value="evolucao" className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico de Evolução por Género */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#0066CC]" />
                Evolução por Género
              </CardTitle>
              <CardDescription>Últimos 12 períodos de referência</CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="periodo" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => v?.slice(0, 7) || ''}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="masculino" 
                      name="Masculino" 
                      stackId="1"
                      stroke="#3b82f6" 
                      fill="#93c5fd" 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="feminino" 
                      name="Feminino" 
                      stackId="1"
                      stroke="#ec4899" 
                      fill="#f9a8d4" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Sem dados históricos disponíveis</p>
                    <p className="text-sm">Submeta dados demográficos para ver a evolução</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Gráfico de Barras Comparativo */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#0066CC]" />
                Comparação de Totais
              </CardTitle>
              <CardDescription>Total de utentes por período</CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="periodo" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => v?.slice(0, 7) || ''}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="masculino" 
                      name="Masculino" 
                      fill="#3b82f6" 
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar 
                      dataKey="feminino" 
                      name="Feminino" 
                      fill="#ec4899" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Sem dados históricos disponíveis</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabela de Histórico Detalhado */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="w-5 h-5 text-[#0066CC]" />
              Histórico Detalhado
            </CardTitle>
          </CardHeader>
          <CardContent>
            {utentes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">Período</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-blue-600">♂ Masculino</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-pink-600">♀ Feminino</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Total</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Variação</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {utentes.slice(0, 12).map((u, i) => {
                      const prevTotal = utentes[i+1]?.total_utentes || 0;
                      const currentTotal = u.total_utentes || 0;
                      const variacao = prevTotal > 0 ? ((currentTotal - prevTotal) / prevTotal * 100).toFixed(1) : 0;
                      
                      return (
                        <tr key={i} className="border-t hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium">{u.periodo_referencia}</td>
                          <td className="px-4 py-3 text-center text-blue-600">{u.total_masculino || 0}</td>
                          <td className="px-4 py-3 text-center text-pink-600">{u.total_feminino || 0}</td>
                          <td className="px-4 py-3 text-center font-semibold">{currentTotal}</td>
                          <td className="px-4 py-3 text-center">
                            {prevTotal > 0 && (
                              <span className={`text-sm ${variacao > 0 ? 'text-green-600' : variacao < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                                {variacao > 0 ? '+' : ''}{variacao}%
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => viewHistoryItem(u)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400">
                <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Sem histórico de dados demográficos</p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
      </Tabs>

      {/* Modal Dados Demográficos */}
      <Dialog open={utentesModalOpen} onOpenChange={setUtentesModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#0066CC]" />
              Dados Demográficos por Faixa Etária
            </DialogTitle>
            <DialogDescription>
              Registe o número de utentes por faixa etária e género
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Período de Referência</Label>
              <Input
                type="month"
                value={utentesForm.periodo_referencia}
                onChange={(e) => setUtentesForm(prev => ({ ...prev, periodo_referencia: e.target.value }))}
              />
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium">Faixa Etária</th>
                    <th className="px-4 py-2 text-center text-sm font-medium">Masculino</th>
                    <th className="px-4 py-2 text-center text-sm font-medium">Feminino</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Bebés 0-12 meses', masc: 'bebes_0_12m_masculino', fem: 'bebes_0_12m_feminino' },
                    { label: 'Bebés 13-24 meses', masc: 'bebes_13_24m_masculino', fem: 'bebes_13_24m_feminino' },
                    { label: 'Crianças 3-5 anos', masc: 'criancas_3_5a_masculino', fem: 'criancas_3_5a_feminino' },
                    { label: 'Crianças 6-10 anos', masc: 'criancas_6_10a_masculino', fem: 'criancas_6_10a_feminino' },
                    { label: 'Jovens 11-14 anos', masc: 'jovens_11_14a_masculino', fem: 'jovens_11_14a_feminino' },
                    { label: 'Jovens 15-18 anos', masc: 'jovens_15_18a_masculino', fem: 'jovens_15_18a_feminino' },
                    { label: 'Adultos 18+ anos', masc: 'adultos_18plus_masculino', fem: 'adultos_18plus_feminino' },
                    { label: 'Idosos 65+ anos', masc: 'idosos_65plus_masculino', fem: 'idosos_65plus_feminino' }
                  ].map((row, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-4 py-2 text-sm">{row.label}</td>
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          min="0"
                          className="w-20 mx-auto h-8 text-center"
                          value={utentesForm[row.masc]}
                          onChange={(e) => updateUtentesField(row.masc, e.target.value)}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          min="0"
                          className="w-20 mx-auto h-8 text-center"
                          value={utentesForm[row.fem]}
                          onChange={(e) => updateUtentesField(row.fem, e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t bg-slate-50 font-semibold">
                    <td className="px-4 py-2">TOTAL</td>
                    <td className="px-4 py-2 text-center">{calcularTotais().masculino}</td>
                    <td className="px-4 py-2 text-center">{calcularTotais().feminino}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Total Geral: {calcularTotais().total} utentes</strong>
              </p>
              {Math.abs(calcularTotais().total - resumoAtual.total_utentes) > resumoAtual.total_utentes * 0.1 && (
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ Este total difere mais de 10% do total nas respostas sociais ({resumoAtual.total_utentes})
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUtentesModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmitUtentes} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar Dados
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Dados Demográficos por Resposta Social */}
      <Dialog open={demoModalOpen} onOpenChange={setDemoModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#0066CC]" />
              Dados Demográficos por Faixa Etária
            </DialogTitle>
            <DialogDescription>
              {selectedValencia?.nome}
              {valenciaFaixas.length > 0 && (
                <Badge className="ml-2 bg-blue-100 text-blue-700">{valenciaFaixas.length} faixas etárias</Badge>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {loadingFaixas ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                <span className="ml-2 text-slate-500">A carregar faixas etárias...</span>
              </div>
            ) : valenciaFaixas.length > 0 ? (
              <>
                {/* Tabela de Faixas Dinâmicas */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium">Faixa Etária</th>
                        <th className="px-4 py-2 text-center text-sm font-medium text-blue-600">♂ Masculino</th>
                        <th className="px-4 py-2 text-center text-sm font-medium text-pink-600">♀ Feminino</th>
                      </tr>
                    </thead>
                    <tbody>
                      {valenciaFaixas.map((faixa, i) => (
                        <tr key={faixa.id || i} className="border-t hover:bg-slate-50">
                          <td className="px-4 py-2 text-sm font-medium">{faixa.nome}</td>
                          <td className="px-4 py-2">
                            <Input
                              type="number"
                              min="0"
                              className="w-20 mx-auto h-8 text-center"
                              value={demoFaixasForm[`${faixa.codigo}_masculino`] || 0}
                              onChange={(e) => setDemoFaixasForm(prev => ({ 
                                ...prev, 
                                [`${faixa.codigo}_masculino`]: parseInt(e.target.value) || 0 
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <Input
                              type="number"
                              min="0"
                              className="w-20 mx-auto h-8 text-center"
                              value={demoFaixasForm[`${faixa.codigo}_feminino`] || 0}
                              onChange={(e) => setDemoFaixasForm(prev => ({ 
                                ...prev, 
                                [`${faixa.codigo}_feminino`]: parseInt(e.target.value) || 0 
                              }))}
                            />
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t bg-slate-50 font-semibold">
                        <td className="px-4 py-2">TOTAL</td>
                        <td className="px-4 py-2 text-center text-blue-600">{calcularTotaisFaixas().masculino}</td>
                        <td className="px-4 py-2 text-center text-pink-600">{calcularTotaisFaixas().feminino}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-700">Total Geral:</span>
                    <span className="font-bold text-blue-900">{calcularTotaisFaixas().total} utentes</span>
                  </div>
                </div>
              </>
            ) : (
              /* Fallback - formulário simples M/F */
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="text-blue-500">♂</span> Masculino
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={demoForm.masculino}
                      onChange={(e) => setDemoForm(prev => ({ ...prev, masculino: parseInt(e.target.value) || 0 }))}
                      data-testid="demo-masculino-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="text-pink-500">♀</span> Feminino
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={demoForm.feminino}
                      onChange={(e) => setDemoForm(prev => ({ ...prev, feminino: parseInt(e.target.value) || 0 }))}
                      data-testid="demo-feminino-input"
                    />
                  </div>
                </div>
                
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Total:</span>
                    <span className="font-semibold">{demoForm.masculino + demoForm.feminino} utentes</span>
                  </div>
                  {selectedValencia && selectedValencia.numero_utentes > 0 && 
                   (demoForm.masculino + demoForm.feminino) !== selectedValencia.numero_utentes && (
                    <div className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      O total será atualizado de {selectedValencia.numero_utentes} para {demoForm.masculino + demoForm.feminino}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDemoModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveValenciaDemographics} className="bg-[#0066CC] hover:bg-[#0055AA]" data-testid="save-demo-btn">
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Gravação */}
      <AlertDialog open={confirmSaveOpen} onOpenChange={setConfirmSaveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Alterações</AlertDialogTitle>
            <AlertDialogDescription>
              Tem a certeza que pretende guardar as seguintes alterações?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 max-h-60 overflow-y-auto">
            {getChangesSummary().length > 0 ? (
              <ul className="space-y-2">
                {getChangesSummary().map((change, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className={`font-medium ${change.cor}`}>•</span>
                    <span className="font-medium">{change.valencia}</span>
                    <span className="text-slate-500">- {change.tipo}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-500 text-sm">Alterações nas respostas sociais</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveValencias} className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" />
              Confirmar e Guardar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Detalhes do Histórico */}
      <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#0066CC]" />
              Dados Demográficos - {selectedHistoryItem?.periodo_referencia}
            </DialogTitle>
          </DialogHeader>
          {selectedHistoryItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{selectedHistoryItem.total_masculino || 0}</p>
                      <p className="text-sm text-blue-700">Masculino ♂</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-pink-50 border-pink-200">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-pink-600">{selectedHistoryItem.total_feminino || 0}</p>
                      <p className="text-sm text-pink-700">Feminino ♀</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Faixa Etária</th>
                      <th className="px-3 py-2 text-center">♂</th>
                      <th className="px-3 py-2 text-center">♀</th>
                      <th className="px-3 py-2 text-center">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Bebés 0-12m', m: 'bebes_0_12m_masculino', f: 'bebes_0_12m_feminino' },
                      { label: 'Bebés 13-24m', m: 'bebes_13_24m_masculino', f: 'bebes_13_24m_feminino' },
                      { label: 'Crianças 3-5a', m: 'criancas_3_5a_masculino', f: 'criancas_3_5a_feminino' },
                      { label: 'Crianças 6-10a', m: 'criancas_6_10a_masculino', f: 'criancas_6_10a_feminino' },
                      { label: 'Jovens 11-14a', m: 'jovens_11_14a_masculino', f: 'jovens_11_14a_feminino' },
                      { label: 'Jovens 15-18a', m: 'jovens_15_18a_masculino', f: 'jovens_15_18a_feminino' },
                      { label: 'Adultos 18+', m: 'adultos_18plus_masculino', f: 'adultos_18plus_feminino' },
                      { label: 'Idosos 65+', m: 'idosos_65plus_masculino', f: 'idosos_65plus_feminino' }
                    ].map((row, i) => {
                      const masc = selectedHistoryItem[row.m] || 0;
                      const fem = selectedHistoryItem[row.f] || 0;
                      return (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-2">{row.label}</td>
                          <td className="px-3 py-2 text-center text-blue-600">{masc}</td>
                          <td className="px-3 py-2 text-center text-pink-600">{fem}</td>
                          <td className="px-3 py-2 text-center font-medium">{masc + fem}</td>
                        </tr>
                      );
                    })}
                    <tr className="border-t bg-slate-50 font-semibold">
                      <td className="px-3 py-2">TOTAL</td>
                      <td className="px-3 py-2 text-center text-blue-600">{selectedHistoryItem.total_masculino || 0}</td>
                      <td className="px-3 py-2 text-center text-pink-600">{selectedHistoryItem.total_feminino || 0}</td>
                      <td className="px-3 py-2 text-center">{selectedHistoryItem.total_utentes || 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="text-xs text-slate-500 flex justify-between">
                <span>Fonte: {selectedHistoryItem.fonte_dados || 'Portal'}</span>
                {selectedHistoryItem.created_at && (
                  <span>Submetido em: {new Date(selectedHistoryItem.created_at).toLocaleDateString('pt-PT')}</span>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryModalOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
